import {
  Component,
  computed,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';

import { Store } from '@ngrx/store';

import { TranslateService } from '@ngx-translate/core';

import { FeedRemovedItem } from '../../../models/core.models';

import { AppState } from 'src/app/reducers';
import { selectCurrentObject } from '../../../reducers';

import { NomObjectClass } from 'src/app/core/constants';
import { DialogCloseOptions, DialogCloseReason } from '@sui/dialog';
import { DialogService } from '@sui/dialog';
import { ConfirmDialogComponent } from '../../popups/confirm-dialog/confirm-dialog.component';
import {
  injectFeedRemovedItemsQuery,
  injectRestoreAllRemovedItemsMutation,
  injectRestoreRemovedItemMutation,
} from 'src/app/api/queries/feed.queries';

@Component({
  selector: 'app-removed-items',
  templateUrl: './removed-items.component.html',
  styleUrl: './removed-items.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class RemovedItemsComponent {
  NomObjectClass = NomObjectClass;

  private dialogService = inject(DialogService);

  // This panel is the list's only consumer, so it owns the observer: mounting the
  // Excluded Posts tab is the fetch, and re-opening it refetches.
  private readonly currentObject = this.store.selectSignal(selectCurrentObject);
  private readonly currentObjectId = computed(
    () => this.currentObject()?.id ?? null,
  );
  readonly removedItemsQuery = injectFeedRemovedItemsQuery(
    this.currentObjectId,
  );
  private readonly restoreItemMutation = injectRestoreRemovedItemMutation();
  private readonly restoreAllMutation = injectRestoreAllRemovedItemsMutation();

  constructor(
    private store: Store<AppState>,
    private translateService: TranslateService,
  ) {}

  removeSingleItem(item: FeedRemovedItem): void {
    const feedId = this.currentObjectId();
    if (!feedId) return;

    const title = this.translateService.instant(
      'removed-items.confirmation.title',
    );
    const message = this.translateService.instant(
      'removed-items.confirmation.are-you-sure-you-want-to-delete-this-item',
    );

    const dialogRef = this.dialogService.openSimpleDialog(
      ConfirmDialogComponent,
      {
        data: {
          title,
          message,
        },
        closeOnClick: false,
      },
    );

    dialogRef.closed.subscribe(
      (event: DialogCloseOptions<{ reason: DialogCloseReason }>) => {
        if (event.reason === DialogCloseReason.Confirm) {
          // `mutateAsync().then(…)`, not a per-call `onSuccess`: the broadcast has
          // to survive this panel being closed mid-request.
          // * the hook's onSuccess owns the content refresh
          this.restoreItemMutation
            .mutateAsync({
              feedId,
              objectId: item.object_id,
              objectClassId: item.object_class_id,
            })
            .catch(() => undefined);
        } else if (event.reason === DialogCloseReason.Cancel) {
          console.log(`Action cancelled: ${event.reason}`);
        }
      },
    );
  }

  removeAllItems(): void {
    const feedId = this.currentObjectId();
    if (!feedId) return;

    const title = this.translateService.instant(
      'removed-items.confirmation.title',
    );
    const message = this.translateService.instant(
      'removed-items.confirmation.are-you-sure-you-want-to-delete-all-items',
    );

    const dialogRef = this.dialogService.openSimpleDialog(
      ConfirmDialogComponent,
      {
        data: {
          title,
          message,
        },
        closeOnClick: false,
      },
    );

    dialogRef.closed.subscribe(
      (event: DialogCloseOptions<{ reason: DialogCloseReason }>) => {
        if (event.reason === DialogCloseReason.Confirm) {
          // * the hook's onSuccess owns the content refresh
          this.restoreAllMutation.mutateAsync(feedId).catch(() => undefined);
        } else if (event.reason === DialogCloseReason.Cancel) {
          console.log(`Action cancelled: ${event.reason}`);
        }
      },
    );
  }

  trackByRemovedItem(index: number, item: FeedRemovedItem): string {
    return `${item.object_class_id}-${item.object_id}`;
  }
}
